import { useSelect, useDispatch } from '@wordpress/data';
import { useEffect, useState } from '@wordpress/element';
import { SUBSCRIPTIONS_STORE_NAME as STORE_NAME } from '../constants';
import { WC_CART_STORE_NAME } from '../constants';
import { getSelectedShippingMethod, getSelectedShippingMethodForSubscription } from '../common/helper';
import SubscriptionProductType from '../valueObject/SubscriptionProductType';
import { isDateBookable } from '../subscriptions/subscriptionHelper';
import { getSubscriptionCheckoutData } from '../subscriptions/api';
import { useDebounce } from '@wordpress/compose';

/**
 * Hook to handle subscription shipping method changes.
 * 
 * This hook monitors the shipping method in the cart and resets
 * the reserved slot when the shipping method changes from the one
 * that was used when the slot was reserved.
 * 
 * @return {Object} The current shipping method
 */
const useFetchSubscriptionDataOnShippingMethodChange = () => {
	const [selectedSubscriptionShippingMethod, setSelectedSubscriptionShippingMethod] = useState(null);
	const [selectedRegularShippingMethod, setSelectedRegularShippingMethod] = useState(null);
	const { setBookableDates, setDateYmd, setTimeslot, setReservedSlot, setShippingMethod, setFormattedSubscriptionInterval, setProducts, setSubscriptionData, setConflictMessage, setAreSlotsAllowed } = useDispatch(STORE_NAME);

	const cartData = useSelect(select => select(WC_CART_STORE_NAME).getCartData());

	const handleChange = () => {
		// Ensure cart data is available before proceeding
		if (!cartData) {
			return;
		}

		const newSelectedSubscriptionShippingMethod = getSelectedShippingMethodForSubscription(cartData, 0);
		const newSelectedRegularShippingMethod = getSelectedShippingMethod(0);

		if (newSelectedSubscriptionShippingMethod !== selectedSubscriptionShippingMethod || newSelectedRegularShippingMethod !== selectedRegularShippingMethod) {
			setSelectedSubscriptionShippingMethod(newSelectedSubscriptionShippingMethod);
			setSelectedRegularShippingMethod(newSelectedRegularShippingMethod);
			setShippingMethod(SubscriptionProductType.SUBSCRIPTION, newSelectedSubscriptionShippingMethod);
			setShippingMethod(SubscriptionProductType.REGULAR, newSelectedRegularShippingMethod);

			getSubscriptionCheckoutData(newSelectedRegularShippingMethod, newSelectedSubscriptionShippingMethod).then((data) => {
				setBookableDates(SubscriptionProductType.SUBSCRIPTION, data.subscription.bookable_dates);
				if (!isDateBookable(SubscriptionProductType.SUBSCRIPTION, data.subscription.bookable_dates)) {
					setDateYmd(SubscriptionProductType.SUBSCRIPTION, '');
					setTimeslot(SubscriptionProductType.SUBSCRIPTION, '');
					setReservedSlot(SubscriptionProductType.SUBSCRIPTION, null);

					// These are common for both subscription and regular shipping methods.
					setConflictMessage(data.subscription.conflict);
					setAreSlotsAllowed(data.subscription.slots_allowed);
				}

				if (data.subscription.formatted_interval) {
					setFormattedSubscriptionInterval(data.subscription.formatted_interval);
				}

				setBookableDates(SubscriptionProductType.REGULAR, data.regular.bookable_dates);
				if (!isDateBookable(SubscriptionProductType.REGULAR, data.regular.bookable_dates)) {
					setDateYmd(SubscriptionProductType.REGULAR, '');
					setTimeslot(SubscriptionProductType.REGULAR, '');
					setReservedSlot(SubscriptionProductType.REGULAR, null);
				}

				// Cart data.
				setSubscriptionData(data.subscription?.cart?.subscription);
				setReservedSlot('subscription', data.subscription?.cart?.reserved_slot);
				setReservedSlot('regular', data.regular?.cart?.reserved_slot);
				
				setProducts('subscription', data.subscription?.cart?.products);
				setProducts('regular', data.regular?.cart?.products);

				if (data.subscription?.cart?.reserved_slot) {
					setDateYmd('subscription', data.subscription?.cart?.reserved_slot?.date_ymd);
					setTimeslot('subscription', data.subscription?.cart?.reserved_slot?.timeslot_value);
				}

				if (data.regular?.cart?.reserved_slot) {
					setDateYmd('regular', data.regular?.cart?.reserved_slot?.date_ymd);
					setTimeslot('regular', data.regular?.cart?.reserved_slot?.timeslot_value);
				}
			});
			
		}
	}

	const debouncedHandleChange = useDebounce(handleChange, 300);

	useEffect(() => {
		handleChange();
	}, [cartData]);

	const getSelectedShippingMethodForClassicCheckout = () => { 
		if (jQuery('.shipping_method').is("input[type='radio']")) {
			return jQuery('.shipping_method:checked').val();
		} else {
			// hidden input.
			return jQuery('.shipping_method').val();
		}
	}

	// Classic checkout: listen to shipping method change.
	useEffect(() => {
		// 1. Handle shipping method change via update_checkout trigger.
		let prevShippingMethod = getSelectedShippingMethodForClassicCheckout();
		jQuery(document).on('update_checkout', () => {
			const currentShippingMethod = getSelectedShippingMethodForClassicCheckout();
			if (currentShippingMethod !== prevShippingMethod) {
				prevShippingMethod = currentShippingMethod;
				handleChange();
			}
		});

		// 2. Handle shipping method change via click event.
		jQuery(document).on('change', '.shipping_method', debouncedHandleChange);

		return () => {
			jQuery(document).off('change', '.shipping_method', debouncedHandleChange);
		};
	}, []);
};

export default useFetchSubscriptionDataOnShippingMethodChange;